<script setup lang="ts">
import { computed } from 'vue'
import { useTour } from '../composables/useTour'
import { useTourContent } from '../composables/useTourContent'
import { renderTourMarkdown } from '../composables/tour-markdown'
import TourProgressWidget from './TourProgressWidget.vue'

const tour = useTour()
const { getContent } = useTourContent()

const open = computed({
  get: () => tour.explorePanelOpen.value,
  set: (v: boolean) => (v ? tour.openExplore() : tour.closeExplore()),
})

const content = computed(() => getContent(tour.currentTour.value?.id))
const tutorialHtml = computed(() => (content.value ? renderTourMarkdown(content.value.tutorial) : ''))

function startTour(): void {
  tour.closeExplore()
  tour.startCurrentScreen()
}

function runAi(prompt: string): void {
  tour.closeExplore()
  tour.executeWithAI(prompt)
}
</script>

<template>
  <Teleport to="body">
    <Transition name="tour-explore">
      <aside v-if="open" class="explore" role="dialog" aria-label="Screen guide">
        <header class="explore__head">
          <div>
            <span class="explore__eyebrow">Screen guide</span>
            <h2 class="explore__title">{{ content?.title ?? tour.currentTour.value?.title ?? 'Help' }}</h2>
          </div>
          <button type="button" class="explore__close" aria-label="Close" @click="tour.closeExplore()">
            <i class="fa-solid fa-xmark" />
          </button>
        </header>

        <div class="explore__scroll">
          <template v-if="content">
            <section v-if="content.valueProp" class="explore__section">
              <h3 class="explore__label"><i class="fa-solid fa-compass" /> Why it matters</h3>
              <p class="explore__lead">{{ content.valueProp }}</p>
            </section>

            <section v-if="content.benefits.length" class="explore__section">
              <h3 class="explore__label"><i class="fa-solid fa-bolt" /> What you gain</h3>
              <ul class="explore__benefits">
                <li v-for="b in content.benefits" :key="b"><i class="fa-solid fa-check" /> {{ b }}</li>
              </ul>
            </section>

            <button v-if="tour.hasCurrentTour.value" type="button" class="explore__cta" @click="startTour">
              <i class="fa-solid fa-play" /> Start the guided tour
            </button>

            <section v-if="content.tutorial" class="explore__section">
              <h3 class="explore__label"><i class="fa-solid fa-list-check" /> Quick tutorial</h3>
              <!-- eslint-disable-next-line vue/no-v-html -->
              <div class="explore__prose" v-html="tutorialHtml" />
            </section>

            <section v-if="content.bestPractices.length" class="explore__section">
              <h3 class="explore__label"><i class="fa-solid fa-lightbulb" /> Best practices</h3>
              <ul class="explore__benefits">
                <li v-for="p in content.bestPractices" :key="p"><i class="fa-solid fa-star" /> {{ p }}</li>
              </ul>
            </section>

            <section v-if="content.aiActions.length" class="explore__section">
              <h3 class="explore__label"><i class="fa-solid fa-wand-magic-sparkles" /> Let the AI do it</h3>
              <button
                v-for="a in content.aiActions"
                :key="a.label"
                type="button"
                class="explore__ai"
                @click="runAi(a.prompt)"
              >
                <span>{{ a.label }}</span>
                <i class="fa-solid fa-arrow-right" />
              </button>
            </section>

            <section v-if="content.videos.length" class="explore__section">
              <h3 class="explore__label"><i class="fa-solid fa-circle-play" /> Videos</h3>
              <a
                v-for="v in content.videos"
                :key="v.url"
                :href="v.url"
                target="_blank"
                rel="noopener noreferrer"
                class="explore__video"
              >
                <i class="fa-solid fa-play" /> {{ v.title }}
              </a>
            </section>

            <section v-if="content.faq.length" class="explore__section">
              <h3 class="explore__label"><i class="fa-solid fa-circle-question" /> FAQ</h3>
              <details v-for="f in content.faq" :key="f.q" class="explore__faq">
                <summary>{{ f.q }}</summary>
                <p>{{ f.a }}</p>
              </details>
            </section>
          </template>

          <div v-else class="explore__empty">
            <i class="fa-solid fa-compass" />
            <p>No guide is available for this screen yet.</p>
          </div>

          <section class="explore__section explore__section--progress">
            <h3 class="explore__label"><i class="fa-solid fa-graduation-cap" /> Your training</h3>
            <TourProgressWidget />
          </section>
        </div>
      </aside>
    </Transition>

    <Transition name="tour-fade">
      <div v-if="open" class="explore__scrim" @click="tour.closeExplore()" />
    </Transition>
  </Teleport>
</template>

<style scoped lang="scss">
.explore {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  z-index: 3220;
  width: min(440px, 100vw);
  display: flex;
  flex-direction: column;
  background: rgb(var(--v-theme-surface));
  color: rgb(var(--v-theme-on-surface));
  border-left: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  box-shadow: -12px 0 40px rgba(17, 29, 43, 0.18);

  &__scrim {
    position: fixed;
    inset: 0;
    z-index: 3219;
    background: rgba(10, 20, 32, 0.4);
  }

  &__head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    padding: 22px 24px 16px;
    border-bottom: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  }

  &__eyebrow {
    font-size: 0.68rem;
    font-weight: 700;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: rgb(var(--v-theme-primary));
  }

  &__title {
    font-size: 1.3rem;
    font-weight: 700;
    margin-top: 2px;
  }

  &__close {
    width: 34px;
    height: 34px;
    border-radius: 9px;
    background: transparent;
    color: rgba(var(--v-theme-on-surface), 0.55);
    cursor: pointer;

    &:hover {
      background: rgba(var(--v-theme-on-surface), 0.06);
    }
  }

  &__scroll {
    flex: 1;
    overflow-y: auto;
    padding: 20px 24px 32px;
  }

  &__section {
    margin-bottom: 24px;

    &--progress {
      margin-top: 8px;
      padding-top: 20px;
      border-top: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
    }
  }

  &__label {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 0.72rem;
    font-weight: 700;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: rgba(var(--v-theme-on-surface), 0.55);
    margin-bottom: 10px;

    i {
      color: rgb(var(--v-theme-secondary));
    }
  }

  &__lead {
    font-size: 0.95rem;
    line-height: 1.55;
    color: rgba(var(--v-theme-on-surface), 0.82);
  }

  &__benefits {
    list-style: none;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 8px;

    li {
      display: flex;
      align-items: flex-start;
      gap: 9px;
      font-size: 0.9rem;
      line-height: 1.45;

      i {
        margin-top: 3px;
        font-size: 0.72rem;
        color: rgb(var(--v-theme-success));
      }
    }
  }

  &__cta {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    justify-content: center;
    padding: 12px;
    margin-bottom: 24px;
    border-radius: 12px;
    font-weight: 600;
    color: #fff;
    background: rgb(var(--v-theme-primary));
    cursor: pointer;

    &:hover {
      box-shadow: 0 8px 20px rgba(137, 42, 84, 0.22);
    }
  }

  &__prose {
    font-size: 0.9rem;
    line-height: 1.55;
    color: rgba(var(--v-theme-on-surface), 0.82);

    :deep(ul) {
      padding-left: 18px;
      margin: 6px 0;
    }
    :deep(p) {
      margin: 0 0 8px;
    }
  }

  &__ai {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    gap: 10px;
    padding: 12px 14px;
    margin-bottom: 8px;
    border-radius: 11px;
    font-size: 0.9rem;
    font-weight: 600;
    text-align: left;
    color: rgb(var(--v-theme-on-surface));
    background: rgba(var(--v-theme-primary), 0.08);
    cursor: pointer;

    i {
      color: rgb(var(--v-theme-primary));
      font-size: 0.78rem;
    }
    &:hover {
      background: rgba(var(--v-theme-primary), 0.14);
    }
  }

  &__video {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 12px;
    margin-bottom: 6px;
    border-radius: 10px;
    font-size: 0.9rem;
    text-decoration: none;
    color: rgb(var(--v-theme-on-surface));
    background: rgba(var(--v-theme-on-surface), 0.04);

    i {
      color: rgb(var(--v-theme-secondary));
    }
    &:hover {
      background: rgba(var(--v-theme-on-surface), 0.08);
    }
  }

  &__faq {
    border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
    border-radius: 10px;
    padding: 10px 14px;
    margin-bottom: 8px;

    summary {
      cursor: pointer;
      font-weight: 600;
      font-size: 0.9rem;
    }
    p {
      margin-top: 8px;
      font-size: 0.88rem;
      line-height: 1.5;
      color: rgba(var(--v-theme-on-surface), 0.75);
    }
  }

  &__empty {
    text-align: center;
    padding: 40px 20px;
    color: rgba(var(--v-theme-on-surface), 0.5);

    i {
      font-size: 2rem;
      margin-bottom: 12px;
    }
  }
}

.tour-explore-enter-active,
.tour-explore-leave-active {
  transition: transform 280ms cubic-bezier(0.16, 1, 0.3, 1);
}
.tour-explore-enter-from,
.tour-explore-leave-to {
  transform: translateX(100%);
}

.tour-fade-enter-active,
.tour-fade-leave-active {
  transition: opacity 200ms ease;
}
.tour-fade-enter-from,
.tour-fade-leave-to {
  opacity: 0;
}

@media (prefers-reduced-motion: reduce) {
  .tour-explore-enter-active,
  .tour-explore-leave-active,
  .tour-fade-enter-active,
  .tour-fade-leave-active {
    transition: none;
  }
}
</style>
